import type { IObjectSchema } from '@ksyos/koi';
import type { CustomControlCode, ValidationSchemaVersion } from '@common/enums';
import type { Section } from '@common/formEngine/formEngine.types';
import type { Hooks } from '@common/formEngine/hooks.types';
import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import { createInitialData } from '@common/formEngine/typedSectionData';
import { convertToReadSections } from '@common/formEngine/typedSections';
import { convertToDraftObjectSchema, convertToFinalObjectSchema } from '@common/formEngine/typedSectionSchemas';

export type RegistryEntry<Data extends Record<string, unknown> = Record<string, unknown>> = Readonly<{
    [ValidationSchemaVersion.draft]: IObjectSchema;
    [ValidationSchemaVersion.final]: IObjectSchema;
    hooks?: Hooks;
    initialData?: Data;
    readSections?: TypedReadSchema[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sections?: Section<any>[];
}>;

export type RegisterParams = {
    customControlCode: CustomControlCode;
    hooks?: Hooks;
    version: number;
} & (
    | {
          [ValidationSchemaVersion.draft]: IObjectSchema;
          [ValidationSchemaVersion.final]: IObjectSchema;
          readSections?: TypedReadSchema[];
          sections?: never;
      }
    | {
          [ValidationSchemaVersion.draft]?: never;
          [ValidationSchemaVersion.final]?: never;
          readSections?: never;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Allow for usage with generics
          sections: Section<any, any, any>[];
      }
);

// `any` is necessary here due to the generic `Data` for RegistryEntry
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type VersionMap = Map<number, RegistryEntry<any>>;

type Registry = Map<CustomControlCode, VersionMap>;

/**
 * ServiceActivityRegistry is a singleton and can only be created once.
 */
export class ServiceActivityRegistry {
    private static _instance: ServiceActivityRegistry;
    private readonly registry: Registry;

    private constructor() {
        this.registry = new Map();
    }

    public static get instance(): ServiceActivityRegistry {
        if (!this._instance) {
            this._instance = new ServiceActivityRegistry();
        }

        return this._instance;
    }

    public getRegistry(): Registry {
        return this.registry;
    }

    public register({ customControlCode, draft, final, hooks, readSections, sections, version }: RegisterParams): void {
        if (this.registry.get(customControlCode)?.has(version)) {
            throw new Error(
                `Custom control code "${customControlCode}" and version "${version}" is already registered`
            );
        }

        if (!this.registry.has(customControlCode)) {
            this.registry.set(customControlCode, new Map());
        }

        let entry: RegistryEntry;
        if (sections) {
            entry = {
                draft: convertToDraftObjectSchema(sections),
                final: convertToFinalObjectSchema(sections),
                hooks,
                initialData: createInitialData(sections),
                readSections: convertToReadSections<Record<string, unknown>>(sections),
                sections,
            };
        } else {
            entry = {
                draft,
                final,
                readSections,
            };
        }

        this.registry.get(customControlCode)?.set(version, entry);
    }

    public registerAll(elements: RegisterParams[]): void {
        for (const element of elements) {
            this.register(element);
        }
    }
}
